/**
 * Shared formatting utilities for the Manage dashboard
 */

/**
 * Format pence as GBP currency string
 * @param {number} pence - Amount in pence
 * @returns {string} Formatted currency string (e.g., "£12.50")
 */
export function formatPrice(pence) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(pence / 100)
}

/**
 * Format ISO date string to readable format
 * @param {string} dateString - ISO date string
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export function formatDate(dateString, options = {}) {
  const defaultOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }
  return new Date(dateString).toLocaleDateString('en-GB', { ...defaultOptions, ...options })
}

/**
 * Format ISO date string to short format (no year)
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string
 */
export function formatDateShort(dateString) {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * Order status colors for badges
 */
export const ORDER_STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-800',
  pending_payment: 'bg-gray-100 text-gray-800',
  paid: 'bg-green-100 text-green-800',
  confirmed: 'bg-blue-100 text-blue-800',
  preparing: 'bg-yellow-100 text-yellow-800',
  ready: 'bg-purple-100 text-purple-800',
  collected: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800'
}

/**
 * Booking status colors for badges
 */
export const BOOKING_STATUS_COLORS = {
  confirmed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  no_show: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-gray-100 text-gray-800',
  pending_payment: 'bg-blue-100 text-blue-800'
}

/**
 * Membership role colors for badges
 */
export const ROLE_COLORS = {
  member: 'bg-gray-100 text-gray-800',
  manager: 'bg-purple-100 text-purple-800',
  staff: 'bg-orange-100 text-orange-800',
  admin: 'bg-sky-100 text-sky-800'
}

/**
 * Role descriptions for tooltips/help text
 */
export const ROLE_DESCRIPTIONS = {
  member: 'Basic access, can view content',
  staff: 'Operational access for day-to-day tasks',
  manager: 'Can manage menu, orders, and settings',
  admin: 'Full access including team management'
}

/**
 * Get status badge classes
 * @param {string} status - Status value
 * @param {object} colorMap - Map of status to color classes
 * @returns {string} Tailwind classes for the badge
 */
export function getStatusClasses(status, colorMap = ORDER_STATUS_COLORS) {
  return colorMap[status] || 'bg-gray-100 text-gray-800'
}

/**
 * Pluralize a word based on count
 * @param {number} count - The count
 * @param {string} singular - Singular form
 * @param {string} plural - Plural form (optional, defaults to singular + 's')
 * @returns {string} The appropriate form
 */
export function pluralize(count, singular, plural = null) {
  return count === 1 ? singular : (plural || `${singular}s`)
}

/**
 * Format expiry countdown from ISO date string
 * @param {string} expiresAt - ISO date string
 * @returns {string} Human readable expiry message
 */
export function formatExpiryCountdown(expiresAt) {
  if (!expiresAt) return 'No expiry'

  const now = new Date()
  const expiry = new Date(expiresAt)
  const diffMs = expiry - now
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays <= 0) return 'Expired'
  if (diffDays === 1) return 'Expires tomorrow'
  return `Expires in ${diffDays} days`
}

/**
 * Expiry options for invite form
 */
export const EXPIRY_OPTIONS = [
  { value: '1', label: '1 day' },
  { value: '7', label: '7 days' },
  { value: '14', label: '14 days' },
  { value: '30', label: '30 days' }
]

/**
 * Capitalize first letter of a string
 * Useful for displaying enum values like 'member' -> 'Member'
 * @param {string} str - String to capitalize
 * @returns {string} Capitalized string
 */
export function capitalize(str) {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}
